import { useState, useEffect, useCallback } from 'react';
import { getValidToken } from '../utils/auth';
import { optionTradingViewUrl } from '../utils/tradingViewLink';
import TradingDatePicker from './TradingDatePicker';
import { etTodayStr } from '../utils/tradingCalendar';

const API_URL = process.env.REACT_APP_API_URL || '';

const STATUS_COLOR = { watching: '#f59e0b', open: '#60a5fa', closed: '#9ca3af', expired: '#6b7280' };

function formatTime(unixMs) {
  if (!unixMs) return '—';
  return new Date(unixMs).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true, timeZone: 'America/New_York',
  });
}

function money(v) {
  return v > 0 ? `$${v.toFixed(2)}` : '—';
}

function rsi(v) {
  return v == null ? '—' : v.toFixed(1);
}

// Spread at fill time, in % of the mid - the cost this strategy is most
// exposed to, since it trades contracts priced under $3 where a $0.10
// spread is already several percent.
function spreadPct(bid, ask) {
  if (bid == null || ask == null || bid <= 0 || ask <= 0) return '—';
  const mid = (bid + ask) / 2;
  return mid > 0 ? `${((ask - bid) / mid * 100).toFixed(0)}%` : '—';
}

// How much further price ran past our exit - i.e. what the fixed take-profit
// left on the table.
function missedPct(peak, exit) {
  if (peak == null || exit == null || exit <= 0 || peak <= exit) return '—';
  return `+${((peak - exit) / exit * 100).toFixed(0)}%`;
}

// SPX RSI-cross-up-20 agent (2026-07-24) - real Alpaca paper orders, not
// simulated fills. Sits above CheapBotWidget on /bot per explicit placement
// request.
//
// The fill-quality columns (spread in/out, TP level, peak left behind) were
// added 2026-07-26 to settle an open question the backtests could not: this
// strategy measured +71.5% assuming fills at intra-bar highs and -411.0%
// requiring a full 1-min close above target, over the same 59 trades. Only
// real fills can say which is true, so they are recorded here.
export default function SPXRSIAgentWidget() {
  const [date, setDate] = useState(() => etTodayStr());
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (d) => {
    try {
      const token = await getValidToken();
      const res = await fetch(`${API_URL}/api/spx-rsi-agent?date=${d}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    fetchData(date);
    // Only worth polling live when looking at today - a past date's
    // history won't change underneath you.
    if (date !== etTodayStr()) return;
    const timer = setInterval(() => fetchData(date), 5000);
    return () => clearInterval(timer);
  }, [date, fetchData]);

  const trades = data?.trades || [];
  const realized = trades.filter((t) => t.pnl_pct != null);
  const wins = realized.filter((t) => t.pnl_pct > 0).length;

  const watchPill = (w, label, color) => w && (
    <span className="text-xs px-2 py-1 rounded font-semibold font-mono"
      style={{ backgroundColor: color + '22', color }}>
      {label} watching: {w.symbol} (${w.strike.toFixed(0)}, RSI {rsi(w.signal_rsi)}) — awaiting SPY confirmation
    </span>
  );

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <span className="text-xs font-bold px-3 py-1.5 rounded border"
          style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>
          SPX RSI Agent — Real Paper Orders
        </span>
        {data?.position && (
          <span className="text-xs px-2 py-1 rounded font-semibold font-mono"
            style={{ backgroundColor: '#60a5fa22', color: '#60a5fa' }}>
            OPEN: {data.position.side === 'C' ? 'CALL' : 'PUT'} {data.position.strike.toFixed(0)} @ {money(data.position.entry_price)}
          </span>
        )}
        {watchPill(data?.call_watching, 'CALL', '#22c55e')}
        {watchPill(data?.put_watching, 'PUT', '#ef4444')}
        <div className="flex-1" />
        {realized.length > 0 && (
          <span className="text-xs font-semibold font-mono"
            style={{ color: wins >= realized.length / 2 ? '#22c55e' : '#ef4444' }}>
            {wins}/{realized.length} wins
          </span>
        )}
        <TradingDatePicker value={date} onChange={setDate} />
      </div>
      <div className="text-xs text-gray-500 mb-3">
        Entry: nearest-to-spot CALL/PUT under $3, contract RSI(7,5m) crosses up 20 on a <em>closed</em> candle. SPY confirmation is currently disabled.
        Exit: a +30% sell limit <em>resting in the market</em> from the moment the entry fills (so a one-tick spike still fills), with 60min max hold and a 15:55 flatten as backstops — no stop-loss.
        Real Alpaca paper orders. "Spread in/out" is the bid-ask as % of mid; "Peak left" is how far price ran past our exit.
      </div>
      {error && <div className="text-red-400 text-sm mb-3">Failed to load: {error}</div>}

      <div className="overflow-x-auto">
        <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr className="text-gray-500 border-b" style={{ borderColor: '#1f2937' }}>
              {['Side', 'Strike', 'Signal RSI', 'Entry', 'Spread in', 'TP', 'Exit', 'Spread out', 'Peak left', 'Why', 'P&L%', 'Entry Time', 'Exit Time', 'Status'].map((h) => (
                <th key={h} className={`py-2 pr-3 font-medium ${h === 'Side' ? 'text-left' : 'text-right'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trades.length === 0 ? (
              <tr><td colSpan={14} className="py-4 text-center text-gray-600">No trades today</td></tr>
            ) : trades.map((t, i) => (
              <tr key={`${t.symbol}-${i}`} className="border-b hover:bg-gray-800/50" style={{ borderColor: '#1f2937' }}>
                <td className="py-1.5 pr-3">
                  <span className="font-semibold px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: (t.side === 'C' ? '#22c55e' : '#ef4444') + '22', color: t.side === 'C' ? '#22c55e' : '#ef4444' }}>
                    {t.side === 'C' ? 'CALL' : 'PUT'}
                  </span>
                </td>
                <td className="py-1.5 pr-3 text-right font-mono">
                  <a href={optionTradingViewUrl(t.symbol)} target="_blank" rel="noopener noreferrer"
                    title="Open on TradingView"
                    style={{ color: '#60a5fa', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
                    {t.strike.toFixed(0)}
                  </a>
                </td>
                <td className="py-1.5 pr-3 text-right font-mono text-gray-300">{rsi(t.signal_rsi)}</td>
                <td className="py-1.5 pr-3 text-right font-mono text-gray-300">{money(t.entry_price)}</td>
                <td className="py-1.5 pr-3 text-right font-mono text-gray-500" title="bid/ask spread as % of mid at entry">{spreadPct(t.entry_bid, t.entry_ask)}</td>
                <td className="py-1.5 pr-3 text-right font-mono" style={{ color: '#22c55e' }} title="where the resting sell limit was posted">{money(t.tp_price)}</td>
                <td className="py-1.5 pr-3 text-right font-mono text-gray-300">{money(t.exit_price)}</td>
                <td className="py-1.5 pr-3 text-right font-mono text-gray-500" title="bid/ask spread as % of mid at exit">{spreadPct(t.exit_bid, t.exit_ask)}</td>
                <td className="py-1.5 pr-3 text-right font-mono" style={{ color: '#f59e0b' }} title="how far past our exit the price went - what the fixed TP left behind">{missedPct(t.peak_price, t.exit_price)}</td>
                <td className="py-1.5 pr-3 text-right text-gray-400">{t.exit_reason || '—'}</td>
                {t.pnl_pct == null ? (
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-500">—</td>
                ) : (
                  <td className="py-1.5 pr-3 text-right font-mono font-semibold"
                    style={{ color: t.pnl_pct >= 0 ? '#22c55e' : '#ef4444' }}>
                    {t.pnl_pct >= 0 ? '+' : ''}{t.pnl_pct.toFixed(1)}%
                  </td>
                )}
                <td className="py-1.5 pr-3 text-right text-gray-400">{formatTime(t.entry_at)}</td>
                <td className="py-1.5 pr-3 text-right text-gray-400">{formatTime(t.exit_at)}</td>
                <td className="py-1.5 pr-3 text-right">
                  <span className="font-semibold px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: (STATUS_COLOR[t.status] || '#6b7280') + '22', color: STATUS_COLOR[t.status] || '#6b7280' }}>
                    {t.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
